// Public-facing agent metadata for the landing page.
//
// Source of truth: STRIKZA_DualSwarm_Architecture v1.0.0 + ACC v1.0.
// 18 agents across 2 LLM swarms (Claude + OpenAI) + 4 shared ops.
//
// Doctrinal posture:
//   - SWARM 1 (Claude) handles every daily slip cycle (≤4 legs), the
//     adversarial-debate layer, and the
//     subscriber advisory surface. Multi-turn reasoning, nuanced
//     confidence scoring, Kelly-sized stakes.
//   - SWARM 2 (OpenAI · structured + reasoning tiers) handles
//     POWERBALL 10, MEGA POWERBALL 20, heavy accumulators (5+ legs),
//     season-long markets, and the high-stakes parallel audit when a
//     user's float crosses GH₵10,000.
//   - SHARED OPS (OpenAI · structured) are platform services that fire
//     on schedule — slip publication, dedup, settlement, stake calc.
//
// Forward-looking copy: agents not yet wired in code are still listed
// here because the doctrine is the source of truth. The landing
// reflects the documented engine, not the half-built state.

export type SwarmId = "swarm_1" | "swarm_2" | "shared_ops";

export interface Agent {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly desc: string;
  readonly glyph: string;
  readonly swarm: SwarmId;
  readonly provider: "Claude" | "OpenAI";
  readonly model: string;
}

export interface Swarm {
  readonly id: SwarmId;
  readonly name: string;
  readonly subtitle: string;
  readonly provider: "Claude" | "OpenAI";
  readonly model: string;
  readonly purpose: string;
  readonly strength: string;
  readonly agents: readonly Agent[];
}

// Public copy uses a generic capability descriptor, NOT a model snapshot —
// naming the exact model in marketing is borderline methodology exposure
// (public_communication_rules.md). The concrete pinned ID lives in internal
// config only (src/agents/registry.ts STRIKZA_ANTHROPIC_MODEL_ID).
const CLAUDE_MODEL = "Claude · frontier reasoning";
// Generic capability descriptors for public copy (see CLAUDE_MODEL above) — the
// landing names provider + tier, never a concrete model snapshot.
const OPENAI_STANDARD = "GPT · structured";
const OPENAI_REASONING = "GPT · reasoning";

const SWARM_1_AGENTS: readonly Agent[] = [
  {
    id: "scout",
    name: "SCOUT",
    role: "Fixture gate",
    desc: "Scans every fixture in every tracked league and scores exploitability. Nothing else runs until Scout produces an 8-12 fixture shortlist.",
    glyph: "01",
    swarm: "swarm_1",
    provider: "Claude",
    model: CLAUDE_MODEL,
  },
  {
    id: "agent_a_form",
    name: "AGENT A — FORM",
    role: "Recent form, H2H, base rates",
    desc: "Last 5 games · home/away splits · head-to-head history · CLV-adjusted confidence. Cites a specific historical frequency for every claim.",
    glyph: "02",
    swarm: "swarm_1",
    provider: "Claude",
    model: CLAUDE_MODEL,
  },
  {
    id: "agent_b_value",
    name: "AGENT B — VALUE",
    role: "Mispricing detection",
    desc: "Cross-bookmaker odds vs Fair Price Engine probability. Surfaces value edge, line-movement classification (steam · public · trap), and best-platform routing.",
    glyph: "03",
    swarm: "swarm_1",
    provider: "Claude",
    model: CLAUDE_MODEL,
  },
  {
    id: "agent_c_stats",
    name: "AGENT C — STATS",
    role: "Market-specific intelligence",
    desc: "Corners · fouls · BTTS rates · clean-sheet streaks · over/under by venue and referee. Reads the 12 DNA profile tables.",
    glyph: "04",
    swarm: "swarm_1",
    provider: "Claude",
    model: CLAUDE_MODEL,
  },
  {
    id: "agent_d_intel",
    name: "AGENT D — INTEL",
    role: "Real-world context",
    desc: "Confirmed lineups · injuries · weather · travel fatigue · press signals. Re-runs on every lineup change. Skipped for Tier-3 leagues until enough sample exists.",
    glyph: "05",
    swarm: "swarm_1",
    provider: "Claude",
    model: CLAUDE_MODEL,
  },
  {
    id: "debate_moderator",
    name: "DEBATE MODERATOR",
    role: "Adversarial synthesis",
    desc: "Runs 2-3 turns of cross-examination across A/B/C/D. Forces every agent to defend its confidence claim. Eliminates narrative bias and hot takes.",
    glyph: "06",
    swarm: "swarm_1",
    provider: "Claude",
    model: CLAUDE_MODEL,
  },
  {
    id: "consensus_ranker",
    name: "CONSENSUS RANKER",
    role: "Final slip + Kelly stake",
    desc: "Scores selections by frequency × confidence and sizes the stake via Kelly. The only agent that touches the user-facing slip.",
    glyph: "07",
    swarm: "swarm_1",
    provider: "Claude",
    model: CLAUDE_MODEL,
  },
];

const SWARM_2_AGENTS: readonly Agent[] = [
  {
    id: "fixture_scout_gpt",
    name: "FIXTURE SCOUT GPT",
    role: "POWERBALL candidate scan",
    desc: "Scans 40-50 weekend fixtures across 8 leagues. Scores each by jackpot suitability — form momentum, league position, fixture context.",
    glyph: "08",
    swarm: "swarm_2",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
  {
    id: "form_analyst_gpt",
    name: "FORM ANALYST GPT",
    role: "Extended form & momentum",
    desc: "10-game window (vs SWARM 1's 5) · home/away splits · momentum curves · goal-difference trends. Deeper than Agent A for accumulator construction.",
    glyph: "09",
    swarm: "swarm_2",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
  {
    id: "value_hunter_gpt",
    name: "VALUE HUNTER GPT",
    role: "Multi-platform mispricing",
    desc: "Simultaneous odds checks across Stake · Betway · SportyBet. Flags selections where market price is ≥15% off STRIKZA model probability.",
    glyph: "10",
    swarm: "swarm_2",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
  {
    id: "stats_engine_gpt",
    name: "STATS ENGINE GPT",
    role: "Acca-grade market stats",
    desc: "BTTS rates · clean-sheet streaks · over/under by venue and referee · goals-per-15-min windows. Tuned for combinatorial slip construction.",
    glyph: "11",
    swarm: "swarm_2",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
  {
    id: "intel_gpt",
    name: "INTEL GPT",
    role: "Lineups, weather, must-win flags",
    desc: "Lineups · injuries · manager press signals · travel fatigue · weather at venue · must-win-context flags for relegation, title, and continental fixtures.",
    glyph: "12",
    swarm: "swarm_2",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
  {
    id: "acca_architect_gpt",
    name: "ACCA ARCHITECT GPT",
    role: "POWERBALL 10 · MEGA POWERBALL 20",
    desc: "Assembles both jackpot slips simultaneously. Foundation / Mid-Layer / Amplifier structure for PB10. Anchor / Bridge / Stretch for MEGA 20. Escalates to the reasoning tier for the final assembly.",
    glyph: "13",
    swarm: "swarm_2",
    provider: "OpenAI",
    model: OPENAI_REASONING,
  },
  {
    id: "risk_governor_gpt",
    name: "RISK GOVERNOR GPT",
    role: "Cross-slip safety + reserve",
    desc: "Cross-slip deduplication · stake-tier calculation · correlation safety · jackpot reserve balance · weekly exposure cap enforcement.",
    glyph: "14",
    swarm: "swarm_2",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
];

const SHARED_OPS_AGENTS: readonly Agent[] = [
  {
    id: "cross_slip_dedup",
    name: "CROSS-SLIP DEDUP",
    role: "Slip-vs-slip conflict resolver",
    desc: "Ensures no fixture appears on both POWERBALL 10 and MEGA 20 simultaneously. Replaces conflicts with the next-best Pool B candidate.",
    glyph: "15",
    swarm: "shared_ops",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
  {
    id: "stake_calculator",
    name: "STAKE CALCULATOR",
    role: "Tier sizing + reserve",
    desc: "Standard / Conviction / Full Send tier per slip. Reads jackpot reserve. Logs debits. Enforces the weekly combined-exposure cap.",
    glyph: "16",
    swarm: "shared_ops",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
  {
    id: "publisher",
    name: "PUBLISHER",
    role: "Slip distribution",
    desc: "Formats and distributes slips on schedule. App tabs · Telegram · WhatsApp · email. Adds timestamp, week number, and provenance to every published artifact.",
    glyph: "17",
    swarm: "shared_ops",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
  {
    id: "result_logger",
    name: "RESULT LOGGER",
    role: "Settlement + ledger seal",
    desc: "Pulls final results · marks each leg WIN / LOSS / VOID · seals the Daily Intelligence Ledger row at 00:00 GMT · triggers the JACKPOT push when warranted.",
    glyph: "18",
    swarm: "shared_ops",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
  },
];

export const SWARMS: readonly Swarm[] = [
  {
    id: "swarm_1",
    name: "SWARM 1 — CLAUDE",
    subtitle: "Daily slip cycle · Adversarial debate · Subscriber advisory",
    provider: "Claude",
    model: CLAUDE_MODEL,
    purpose: "Every daily slip ≤4 legs runs through this swarm.",
    strength:
      "Multi-turn reasoning · nuanced confidence scoring · adversarial cross-examination · Kelly-sized stake recommendations.",
    agents: SWARM_1_AGENTS,
  },
  {
    id: "swarm_2",
    name: "SWARM 2 — OPENAI",
    subtitle: "POWERBALL 10 · MEGA POWERBALL 20 · Heavy accumulators · Season-long markets",
    provider: "OpenAI",
    model: "GPT · structured + reasoning",
    purpose: "Every weekend jackpot slip and every 5+ leg accumulator runs through this swarm.",
    strength:
      "Fast structured-JSON output · parallel throughput across 40-50 candidates · deterministic rule enforcement · cost-efficient combinatorial assembly.",
    agents: SWARM_2_AGENTS,
  },
  {
    id: "shared_ops",
    name: "SHARED OPS",
    subtitle: "Platform-level services across both swarms",
    provider: "OpenAI",
    model: OPENAI_STANDARD,
    purpose: "These four fire on schedule and serve every pipeline.",
    strength: "Deterministic ops · publication · settlement · ledger sealing.",
    agents: SHARED_OPS_AGENTS,
  },
];

// Flat 18-agent registry for any consumer that wants the full list
// without traversing the swarm hierarchy.
export const AGENTS: readonly Agent[] = [
  ...SWARM_1_AGENTS,
  ...SWARM_2_AGENTS,
  ...SHARED_OPS_AGENTS,
];

// High-stakes dual-audit threshold — when a user's float exceeds this,
// every daily slip runs through BOTH swarms in parallel, and conflicts
// are surfaced to the user as a choice rather than silently resolved.
export const HIGH_STAKES_FLOAT_THRESHOLD_GHS = 10_000;
